import { operationIds } from "@gen/app-access-config";
import type {
  IAdminClientFee,
  ICreateAdminClientFeeBody,
  IPaymentMethod,
  IRate,
  IUpdateAdminClientFeeBody,
} from "@gen/app-query";
import {
  CoreQuery,
  CreateAdminClientFeeBody,
  Query,
  Rate,
  UpdateAdminClientFeeBody,
} from "@gen/app-query";
import MuiLink from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import type {
  GridInitialState,
  GridValueGetter,
  GridValueSetter,
} from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  type ChipOption,
  createChipSelectEditCellRenderer,
  createChipViewCellRenderer,
  createFlagSelectEditCellRenderer,
  createFlagViewCellRenderer,
  EditableDataGrid,
  type EditableDataGridColDef,
  type FlagOption,
} from "~/components/editable_data_grid";
import { PublicSvgIcon } from "~/components/public_svg_icon";
import { useAuthOperations } from "~/contexts/auth_query";
import { adminClientFeeSchema } from "~/entities/client_fee";
import { useCurrencies } from "~/services/money";
import {
  parseMoney,
  renderMoneyWithoutCurrencySymbol,
} from "~/utilities/money";
import { formatDecimalAmount } from "~/utilities/number";
import { config } from "~root/config";

// The `seller_flat` and `seller_percentage` columns use valueGetter/valueSetter
// to access the nested `seller_rate.flat` and `seller_rate.percentage` properties.
// Same for `client_buyer_rate` and `client_seller_rate`.
type FeeRow = Omit<
  IAdminClientFee,
  "id" | "seller_rate" | "client_buyer_rate" | "client_seller_rate"
> & {
  id: string | number;
  seller_rate?: IRate;
  client_buyer_rate?: IRate;
  client_seller_rate?: IRate;
};

const getFlagName = (code: string): string =>
  `${code.slice(0, 2).toLowerCase()}.svg`;

export function ClientFees() {
  const locale = navigator.languages?.[0] ?? config.localization.locale;
  const { canExecuteAdminOperation } = useAuthOperations();
  const clientId = useParams({
    from: "/admin/clients/$clientId/fees",
    select: (p) => p.clientId,
  });

  const canCreateFee = canExecuteAdminOperation(
    operationIds["core_admin.createClientFee"],
  );
  const canUpdateFee = canExecuteAdminOperation(
    operationIds["core_admin.updateClientFee"],
  );
  const canDeleteFee = canExecuteAdminOperation(
    operationIds["core_admin.deleteClientFee"],
  );
  const canEditFees = canCreateFee || canUpdateFee || canDeleteFee;
  const initialState: GridInitialState = {
    sorting: {
      sortModel: [
        {
          field: "charge_config",
          sort: "asc",
        },
      ],
    },
  };

  const { data: clientData } = CoreQuery.useAdmin_getClientQuery(clientId);
  const isStripePlatformSet =
    clientData?.stripe_platform_id !== null &&
    clientData?.stripe_platform_id !== undefined &&
    clientData?.stripe_platform_id !== "";

  const { data: feesData, isFetching: isFeesFetching } =
    CoreQuery.useAdmin_getClientFeesQuery(clientId);

  const [editedFees, setEditedFees] = useState<FeeRow[]>([]);
  const [_editingFees, setEditingFees] = useState<Set<string | number>>(
    new Set(),
  );

  const queryClient = useQueryClient();

  const { mutate: createFee } = CoreQuery.useAdmin_createClientFeeMutation(
    clientId,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientFeesQueryKey(clientId),
        });
      },
      meta: {
        success: "Fee created",
      },
    },
  );

  const { mutate: updateFee } =
    CoreQuery.useAdmin_updateClientFeeMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientFeesQueryKey(clientId),
        });
      },
      meta: {
        success: "Fee updated",
      },
    });

  const { mutate: deleteFee } =
    CoreQuery.useAdmin_deleteClientFeeMutationWithParameters({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: CoreQuery.admin_getClientFeesQueryKey(clientId),
        });
      },
      meta: {
        success: "Fee deleted",
      },
    });

  useEffect(() => {
    setEditedFees([]);
    setEditingFees(new Set());
  }, [feesData]);

  const { currencies } = useCurrencies();

  const currencyFlagOptions = useMemo<FlagOption[]>(() => {
    if (!currencies || currencies.length === 0) {
      return [];
    }
    return currencies.map((currency) => {
      const value = currency.value ?? currency.code;
      return {
        value,
        label: currency.label,
        flagSrc: `/country_flags/${getFlagName(currency.code)}`,
      };
    });
  }, [currencies]);

  const currencyFlagOptionMap = useMemo(() => {
    const map = new Map<string, FlagOption>();
    currencyFlagOptions.forEach((option) => {
      if (option.value.length === 0) {
        return;
      }
      map.set(option.value, option);
    });
    return map;
  }, [currencyFlagOptions]);

  const currencyFlagViewCell = useMemo(
    () => createFlagViewCellRenderer<FeeRow>(currencyFlagOptionMap),
    [currencyFlagOptionMap],
  );

  const currencyFlagEditCell = useMemo(
    () => createFlagSelectEditCellRenderer<FeeRow>(currencyFlagOptions),
    [currencyFlagOptions],
  );

  const { data: paymentMethodsData } =
    Query.useCore_getSupportedTransactionPaymentMethodsQuery();

  const paymentMethodChipOptions = useMemo<ChipOption[]>(() => {
    const methods = paymentMethodsData?.payment_methods ?? [];

    const toChipOption = (method: IPaymentMethod | null | undefined) => {
      if (!method) {
        return null;
      }
      const code = method.code;
      if (!code || code.length === 0) {
        return null;
      }
      return {
        value: code,
        label: method.label ?? code,
        fontColor: method.font_color ?? undefined,
        backgroundColor: method.background_color ?? undefined,
      };
    };

    return methods.reduce<ChipOption[]>((acc, method) => {
      const option = toChipOption(method);
      if (option) {
        acc.push(option);
      }
      return acc;
    }, []);
  }, [paymentMethodsData?.payment_methods]);
  const paymentMethodChipOptionMap = useMemo(() => {
    const map = new Map<string, ChipOption>();
    paymentMethodChipOptions.forEach((option) => {
      if (option.value.length === 0) {
        return;
      }
      map.set(option.value, option);
    });
    return map;
  }, [paymentMethodChipOptions]);

  const paymentMethodChipViewCell = useMemo(
    () => createChipViewCellRenderer<FeeRow>(paymentMethodChipOptionMap),
    [paymentMethodChipOptionMap],
  );

  const paymentMethodChipEditCell = useMemo(
    () => createChipSelectEditCellRenderer<FeeRow>(paymentMethodChipOptions),
    [paymentMethodChipOptions],
  );

  const paymentMethods = useMemo(
    () =>
      paymentMethodChipOptions.map(({ value, label }) => ({
        value,
        label,
      })),
    [paymentMethodChipOptions],
  );

  const { data: paymentMethodConfigsData } =
    CoreQuery.useAdmin_getPaymentMethodConfigsQuery({
      client_id: clientId,
      account_id: clientData?.on_behalf_of_stripe_connected_account_id || null,
    });
  const paymentMethodConfigs = useMemo(
    () => [
      { value: "", label: "None" },
      ...(paymentMethodConfigsData?.payment_method_configs ?? []).map(
        (paymentMethodConfig) => ({
          value: paymentMethodConfig.code,
          label: paymentMethodConfig.label,
          is_default: paymentMethodConfig.is_default,
        }),
      ),
    ],
    [paymentMethodConfigsData?.payment_method_configs],
  );
  // NOTE `seller_rate` is implemented as an instance of the class
  // `Rate2` inside of the interface `IClientFee2`. The class `Rate2`
  // contains `init` and `toJSON` non-static methods, which
  // have to be omitted when manipulating with the data.
  const parseLocaleNumber = (value: unknown): number =>
    parseFloat(String(value).replace(",", "."));

  const stripePaymentMethodConfigGetter: GridValueGetter<FeeRow, string> = (
    _,
    row,
  ) => row.stripe_payment_method_config_id ?? "";

  const stripePaymentMethodConfigSetter: GridValueSetter<
    FeeRow,
    string | undefined
  > = (value, row) => ({
    ...row,
    stripe_payment_method_config_id: value === "" ? undefined : value,
  });

  const columns: EditableDataGridColDef<FeeRow>[] = useMemo(
    () => [
      {
        field: "charge_config",
        headerName: "Config",
        editable: canEditFees,
        type: "number",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 60,
        minWidth: 60,
        disableColumnMenu: true,
        sortable: false,
        headerClassName: "no-sort-icon",
      },
      {
        field: "payment_method",
        headerName: "Payment Method",
        editable: canEditFees,
        type: "singleSelect",
        flex: 150,
        minWidth: 150,
        valueOptions: paymentMethods,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        renderViewCell: paymentMethodChipViewCell,
        renderEditCell: paymentMethodChipEditCell,
      },
      {
        field: "currency",
        headerName: "Currency",
        editable: canEditFees,
        type: "singleSelect",
        flex: 175,
        minWidth: 175,
        valueOptions: currencies,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        renderViewCell: currencyFlagViewCell,
        renderEditCell: currencyFlagEditCell,
      },
      {
        field: "above",
        headerName: "Above",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 118,
        minWidth: 118,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.above ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return { ...row, above: 0 };
          }
          return {
            ...row,
            above: parseMoney(String(value), row.currency, locale),
          };
        },
      },
      {
        field: "flat",
        headerName: "Buyer Trustap Fee Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(row.flat ?? 0, row.currency, locale),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return { ...row, flat: 0 };
          }
          return {
            ...row,
            flat: parseMoney(String(value), row.currency, locale),
          };
        },
      },
      {
        field: "percentage",
        headerName: "Buyer Trustap Fee %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          percentage: parseLocaleNumber(value),
        }),
      },
      {
        field: "seller_flat",
        headerName: "Seller Trustap Fee Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.seller_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return row;
          }
          return {
            ...row,
            seller_rate: {
              ...(row.seller_rate ?? {}),
              flat: parseMoney(String(value), row.currency, locale),
            } as Rate,
          };
        },
      },
      {
        field: "seller_percentage",
        headerName: "Seller Trustap Fee %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 172,
        minWidth: 172,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.seller_rate?.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          seller_rate: {
            ...(row.seller_rate ?? {}),
            percentage: parseLocaleNumber(value),
          } as IRate,
        }),
      },
      {
        field: "client_buyer_flat",
        headerName: "Buyer Commission Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 110,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.client_buyer_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return row;
          }
          return {
            ...row,
            client_buyer_rate: {
              ...(row.client_buyer_rate ?? {}),
              flat: parseMoney(String(value), row.currency, locale),
              percentage: row.client_buyer_rate?.percentage ?? 0,
            } as IRate,
          };
        },
      },
      {
        field: "client_buyer_percentage",
        headerName: "Buyer Commission %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.client_buyer_rate?.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          client_buyer_rate: {
            ...(row.client_buyer_rate ?? {}),
            percentage: parseLocaleNumber(value),
          } as Rate,
        }),
      },
      {
        field: "client_seller_flat",
        headerName: "Seller Commission Flat",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          renderMoneyWithoutCurrencySymbol(
            row.client_seller_rate?.flat ?? 0,
            row.currency,
            locale,
          ),
        valueSetter: (value: unknown, row: FeeRow) => {
          if (!row.currency) {
            return row;
          }
          return {
            ...row,
            client_seller_rate: {
              ...(row.client_seller_rate ?? {}),
              flat: parseMoney(String(value), row.currency, locale),
            } as Rate,
          };
        },
      },
      {
        field: "client_seller_percentage",
        headerName: "Seller Commission %",
        headerAlign: "right" as const,
        align: "right" as const,
        flex: 160,
        minWidth: 160,
        editable: canEditFees,
        disableColumnMenu: true,
        sortable: false,
        valueGetter: (_value: unknown, row: FeeRow) =>
          formatDecimalAmount(row.client_seller_rate?.percentage ?? 0, locale),
        valueSetter: (value: unknown, row: FeeRow) => ({
          ...row,
          client_seller_rate: {
            ...(row.client_seller_rate ?? {}),
            percentage: parseLocaleNumber(value),
          } as Rate,
        }),
      },
      {
        field: "stripe_payment_method_config_id",
        renderHeader: () => (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <span>PMC</span>
            <Tooltip
              title={
                "A Payment Method Configuration (PMC) is a custom collection of payment methods defined in the Stripe dashboard. These configurations determine which payment options are displayed to the buyer on the payment page.\n\nNote: A Stripe platform account must be assigned to the client in order to assign a PMC in the fee structure."
              }
            >
              <PublicSvgIcon
                name="info"
                data-testid="client-fees-pmc-info-icon"
                sx={{ verticalAlign: "bottom" }}
              />
            </Tooltip>
          </Stack>
        ),
        editable: canEditFees && isStripePlatformSet,
        type: "singleSelect",
        flex: 165,
        minWidth: 165,
        valueOptions: paymentMethodConfigs,
        disableColumnMenu: true,
        sortable: false,
        valueSetter: stripePaymentMethodConfigSetter,
        valueGetter: stripePaymentMethodConfigGetter,
        renderCell: (params) =>
          paymentMethodConfigs.find((opt) => opt.value === params.value)
            ?.label || "Invalid value",
      },
    ],
    [
      paymentMethods,
      paymentMethodConfigs,
      canEditFees,
      isStripePlatformSet,
      paymentMethodChipViewCell,
      paymentMethodChipEditCell,
      currencies,
      currencyFlagViewCell,
      currencyFlagEditCell,
      locale,
    ],
  );

  const displayFees = useMemo(() => {
    const originalFees = feesData?.fees || [];
    const newFees = editedFees.filter(
      (fee) => !originalFees.some((original) => original.id === fee.id),
    );
    return [...originalFees, ...newFees];
  }, [feesData?.fees, editedFees]);

  const handleRowsChange = useCallback(
    (fees: FeeRow[]) => {
      if (!canEditFees) {
        return;
      }

      const originalFeeIds = new Set(
        feesData?.fees.map((f) => f.id as string | number) || [],
      );
      const newEditingFees = new Set<string | number>();

      fees.forEach((fee) => {
        if (!originalFeeIds.has(fee.id)) {
          newEditingFees.add(fee.id);
        }
      });

      setEditingFees(newEditingFees);

      const filteredFees = fees.filter((fee) => {
        const isOriginal = originalFeeIds.has(fee.id);
        const isBeingEdited = newEditingFees.has(fee.id);
        return !isOriginal || isBeingEdited;
      });

      setEditedFees(filteredFees);
    },
    [canEditFees, feesData?.fees],
  );

  const createEmptyRow = (): FeeRow => ({
    id: crypto.randomUUID(),
    client_id: clientId,
    charge_config: 1,
    payment_method: "",
    currency: "",
    above: 0,
    flat: 0,
    percentage: 0,
    stripe_payment_method_config_id: "",
  });

  const handleFeeSave = useCallback(
    (fee: FeeRow) => {
      if (!canEditFees) {
        return;
      }

      const original = feesData?.fees.find((f) => f.id === fee.id);

      if (!original && canCreateFee) {
        const body: ICreateAdminClientFeeBody = {
          charge_config: fee.charge_config,
          stripe_payment_method_config_id: fee.stripe_payment_method_config_id,
          payment_method: fee.payment_method,
          currency: fee.currency,
          above: fee.above,
          flat: fee.flat,
          percentage: fee.percentage,
          round_up_to_nearest: fee.round_up_to_nearest,
          seller_rate: Rate.fromJS({
            flat: fee.seller_rate?.flat ?? 0,
            percentage: fee.seller_rate?.percentage ?? 0,
          }),
          client_buyer_rate: Rate.fromJS({
            flat: fee.client_buyer_rate?.flat ?? 0,
            percentage: fee.client_buyer_rate?.percentage ?? 0,
          }),
          client_seller_rate: Rate.fromJS({
            flat: fee.client_seller_rate?.flat ?? 0,
            percentage: fee.client_seller_rate?.percentage ?? 0,
          }),
        };
        createFee(CreateAdminClientFeeBody.fromJS(body));
      } else if (original && canUpdateFee) {
        const hasChanges =
          fee.charge_config !== original.charge_config ||
          fee.stripe_payment_method_config_id !==
            original.stripe_payment_method_config_id ||
          fee.payment_method !== original.payment_method ||
          fee.currency !== original.currency ||
          fee.above !== original.above ||
          fee.flat !== original.flat ||
          fee.percentage !== original.percentage ||
          fee.round_up_to_nearest !== original.round_up_to_nearest ||
          (fee.seller_rate?.flat ?? null) !==
            (original.seller_rate?.flat ?? null) ||
          (fee.seller_rate?.percentage ?? null) !==
            (original.seller_rate?.percentage ?? null) ||
          (fee.client_buyer_rate?.flat ?? null) !==
            (original.client_buyer_rate?.flat ?? null) ||
          (fee.client_buyer_rate?.percentage ?? null) !==
            (original.client_buyer_rate?.percentage ?? null) ||
          (fee.client_seller_rate?.flat ?? null) !==
            (original.client_seller_rate?.flat ?? null) ||
          (fee.client_seller_rate?.percentage ?? null) !==
            (original.client_seller_rate?.percentage ?? null);

        if (hasChanges) {
          const body: IUpdateAdminClientFeeBody = {
            charge_config: fee.charge_config,
            stripe_payment_method_config_id:
              fee.stripe_payment_method_config_id,
            payment_method: fee.payment_method,
            currency: fee.currency,
            above: fee.above,
            flat: fee.flat,
            percentage: fee.percentage,
            round_up_to_nearest: fee.round_up_to_nearest,
            seller_rate: Rate.fromJS({
              flat: fee.seller_rate?.flat ?? 0,
              percentage: fee.seller_rate?.percentage ?? 0,
            }),
            client_buyer_rate: Rate.fromJS({
              flat: fee.client_buyer_rate?.flat ?? 0,
              percentage: fee.client_buyer_rate?.percentage ?? 0,
            }),
            client_seller_rate: Rate.fromJS({
              flat: fee.client_seller_rate?.flat ?? 0,
              percentage: fee.client_seller_rate?.percentage ?? 0,
            }),
          };
          updateFee({
            client_id: clientId,
            fee_id: fee.id as number,
            updateAdminClientFeeBody: UpdateAdminClientFeeBody.fromJS(body),
          });
        }
      }

      setEditingFees((prev) => {
        const next = new Set(prev);
        next.delete(fee.id as string | number);
        return next;
      });
    },
    [
      canEditFees,
      canCreateFee,
      canUpdateFee,
      feesData?.fees,
      createFee,
      updateFee,
      clientId,
    ],
  );

  const handleFeeDelete = useCallback(
    (fee: FeeRow) => {
      if (!canEditFees) {
        return;
      }

      const original = feesData?.fees.find((f) => f.id === fee.id);
      if (original && canDeleteFee) {
        deleteFee({ client_id: clientId, fee_id: fee.id as number });
      }

      setEditingFees((prev) => {
        const next = new Set(prev);
        next.delete(fee.id as string | number);
        return next;
      });

      setEditedFees((prev) => prev.filter((f) => f.id !== fee.id));
    },
    [canEditFees, canDeleteFee, feesData?.fees, deleteFee, clientId],
  );

  return (
    <>
      <Stack spacing={{ xs: 2, md: 3 }}>
        <EditableDataGrid<FeeRow>
          dataGridTestId="client-fees-grid"
          columns={columns}
          rows={displayFees}
          onRowsChange={handleRowsChange}
          createEmptyRow={createEmptyRow}
          loading={isFeesFetching}
          headingIconName="domain_verification"
          heading="Client Fees"
          addButtonLabel="Add a Fee"
          addButtonDataTestId="add-client-fee-button"
          canAdd={canCreateFee}
          canEdit={canUpdateFee}
          initialState={initialState}
          canDelete={canDeleteFee}
          zodSchema={adminClientFeeSchema}
          onSave={handleFeeSave}
          onDelete={handleFeeDelete}
          noRowsLabel="No Client Fees"
          deleteDialogHeading="Delete Fee"
          getDeleteDialogMessage={(row) =>
            `Are you sure you want to delete the Fee with Configuration ${
              row.charge_config
            } for ${
              paymentMethodConfigs.find(
                (pm) => pm.value === row.payment_method,
              ) ?? row.payment_method
            } Payment Method?`
          }
          deleteDialogConfirmText="Yes, delete Fee"
          deleteDialogCancelText="No, cancel"
        />
        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <MuiLink
            component={Link}
            to={`/admin/clients/${clientId}`}
            data-testid="back-to-client-link"
          >
            Back to Client
          </MuiLink>
        </Stack>
      </Stack>
    </>
  );
}
